import { Classes, FormGroup } from "@blueprintjs/core";
import { Entity, Field } from "typegraph";

import { ID } from "common/entities/IEntity";
import DropdownSelect from "common/form/widgets/common/DropdownSelect";
import IOption from "common/models/IOption";
import TokenSession from "core/util/session/TokenSession";
import EntitySelect from "../../entitySelect/EntitySelect";
import { ConditionTypeRegistry } from "../ConditionTypeRegistry";
import { IConditionType } from "../IConditionType";

type ListOperator = "ANY" | "ALL" | "ONLY" | "NONE";

interface IGroupsCondition {
	operator: ListOperator;
	names: string[];
}

@Entity({many: "groups"})
class Group {
	@Field() public id: ID;
	@Field() public name: string;
}

// TODO value as external ID
const GroupsEntitySelect = EntitySelect.withOptions<Group, string, "many">({
	entity: Group,
	type: 'many',
	getValue: (group) => group.name,
	getDefaultValueParams: (names) => ({names}),
	sortable: false
});

const operators: Array<IOption<ListOperator>> = [
	{value: 'ANY', label: 'Any'},
	{value: 'ALL', label: 'All'},
	{value: 'ONLY', label: "Only"}, // user ONLY has groups in this list, no others.
	{value: 'NONE', label: 'None'}
];

const GroupsConditionType: IConditionType<IGroupsCondition> = {
	type: "groups",
	label: "Groups",
	icon: "person",
	evaluate: ({operator, names}) => {
		if (!names || names.length < 1) {
			return true;
		}

		const user = TokenSession.getUser();

		if (!user) {
			return false;
		}

		const groups = user.getGroups();

		switch(operator) {
			case 'ALL':
				for (const name of names) {
					if (groups.indexOf(name) < 0) {
						return false;
					}
				}
				return true;
			case 'NONE':
				for (const name of names) {
					if (groups.indexOf(name) > -1) {
						return false;
					}
				}
				return true;
			case 'ONLY':
				return groups.every((group) => names.indexOf(group) > -1);
			case 'ANY':
				return names.some((name) => groups.indexOf(name) > -1);
			default:
				return true;
		}
	},
	renderField: (value, onChange) => (
		<div>
			<FormGroup label="Operator">
				<DropdownSelect
					options={operators}
					onChange={(operator) => onChange({...value, operator})}
					value={value.operator}
				/>
			</FormGroup>
			<FormGroup label="Groups" helperText={`User will be required to have ${value.operator} of these Groups`}>
				<GroupsEntitySelect
					formData={value.names || []}
					onChange={(names) => onChange({...value, names})}
					uiSchema={undefined}
					idSchema={undefined}
				/>
			</FormGroup>
		</div>
	),
	renderPreview: (value) => (
		<div>
			{`User has ${value.operator} of groups: ${value.names && value.names.join(', ')}`}
			<div className={Classes.TEXT_MUTED}>{`Current user has groups: ${TokenSession.getUserGroups().join(', ')}`}</div>
		</div>
	),
	getInitialValue: () => ({operator: "ANY", names: []})
};

export default ConditionTypeRegistry.register(GroupsConditionType);



// WEBPACK FOOTER //
// ./src/common/form/fields/condition/conditionTypes/GroupsCondition.reg.tsx